/* eslint-disable @typescript-eslint/naming-convention */
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types.internal";
import { ApiMenuCartItem } from "../LocalCart";
import { ReviewedCartApiResponse } from "../ReviewedCart";

export interface ItemValidVariantsV2Variations {
    default: number;
    default_dependent_variant: {
        group_id: string;
        third_party_group_id: string;
        third_party_variation_id: string;
        variation_id: string;
    };
    id: string;
    inStock: number;
    isVeg: number;
    name: string;
    order: number;
    price: number;
    priceUpdated: boolean;
    variant_group_id: string;
}

export enum VEG_TYPE {
    NON_VEG,
    VEG,
    EGG,
}

export interface ItemValidVariantsV2 {
    pricing_models?: {
        addon_combinations: {
            addon_id: string;
            group_id: string;
            third_party_addon_id: string;
            third_party_group_id: string;
        }[];
        price: number;
        final_price?: number;
        priceUpdated: boolean;
        variations: {
            group_id: string;
            third_party_group_id: string;
            third_party_variation_id: string;
            variation_id: string;
        }[];
    }[];
    variant_groups?: {
        group_id: string;
        name: string;
        variations: ItemValidVariantsV2Variations[];
    }[];
}

export interface Choices {
    default: number;
    id: number;
    inStock: number;
    isVeg: number;
    name: string;
    order: number;
    price: number;
    third_party_group_id: string;
    third_party_id: string;
    social_marker_type?: string;
}

export interface ItemValidAddons {
    choices: Choices[];
    group_id: number;
    group_name: string;
    maxAddons: number;
    maxFreeAddons: number;
    minAddons: number;
    order: number;
    third_party_id: string;
}

export interface ItemValidVariantsVariations {
    default: number;
    externalVariantId: string;
    id: string;
    inStock: number;
    isVeg: number;
    name: string;
    order: number;
    price: number;
    priceUpdated: boolean;
    third_party_variation_id: string;
}

export interface ItemValidVariants {
    exclude_list?: [];
    variant_groups?: {
        group_id: string;
        name: string;
        variations: ItemValidVariantsVariations[];
    }[];
}

export interface ItemVariants {
    external_choice_id: string;
    group_id: number;
    name: string;
    price: number;
    variant_tax_charges?: { GST: string };
    variation_id: number;
}

export interface ItemAddons {
    addon_tax_charges?: { GST: string };
    choice_id: string;
    external_choice_id?: string;
    group_id: string;
    name: string;
    price: number;
}

export enum REWARD_TYPE {
    FREEBIE = "Freebie",
}

export enum FREEBIETYPE {
    OPTIN = "optIn",
}

export enum ItemOrderabilityStatus {
    ORDERABLE = "ORDERABLE",
    NOT_ORDERABLE = "NOT_ORDERABLE",
}

export enum ItemOrderabilityReason {
    INVALID = "INVALID",
    ITEM_OUT_OF_STOCK = "ITEM_OUT_OF_STOCK",
    UNSERVICEABLE = "UNSERVICEABLE",
}

export interface ToingItemAttributes {
    pc_free_cash_qty_dec_popup?: boolean;
}

export interface CartItem {
    item_id?: string;
    added_by_user_id: number;
    addons: ItemAddons[];
    addons_price: string;
    base_price: number;
    cloudinaryImageId: string;
    effective_item_price: number;
    final_price: number;
    free_item_quantity: number;
    group_user_item_map: {};
    GST_details: { CGST: number; IGST: number; SGST: number };
    id_hash: string;
    in_stock: number;
    item_orderability_status?: {
        status: ItemOrderabilityStatus;
        reason?: ItemOrderabilityReason;
    };
    is_gstInclusive: boolean;
    item_group_tag_id?: string | null;
    isVeg: VEG_TYPE;
    item_taxes: { service_tax: number; vat: number; service_charges: number; gst: number };
    mealQuantity: number;
    menu_item_id: number;
    name: string;
    new_item_price: number;
    packing_charge: number;
    quantity: number;
    rewardType: unknown;
    freebieType: FREEBIETYPE | null;
    item_offer_tags?: string[];
    strike_off_enabled: boolean;
    subtotal: number;
    subtotal_coupon_discount: number;
    subtotal_trade_discount: number;
    total: number;
    valid_addons: ItemValidAddons[] | null;
    valid_variants: ItemValidVariants | null;
    valid_variants_v2: ItemValidVariantsV2 | null;
    variants: ItemVariants[];
    variants_price: string;
    costV2?: number;
    defaultPrice?: number;
    price?: number;
    isVersion2?: boolean;
    description?: string;
    hide_customisation?: boolean;
    item_price_strike_off?: boolean;
    default_variant_combination_final_price?: number;
    disable_increase_qty?: boolean;
    item_limit_reached_message?: string;
    itemTags?: string[];
    offer_ids?: string;
    item_offer_cart_meta?: string;
    customer_request_messages?: string[];
    cart_context?: string; // user intent
    color?: string;
    item_discount_percentage?: number;
    toing_item_attributes?: ToingItemAttributes;
}

export interface CartItemWithUUID extends CartItem {
    unId: string;
    itemPosition: number;
}

export enum ItemImagesOnCartXPVariants {
    DEFAULT = "default",
    BIG = "large_image_on_right",
    SMALL = "small_image_on_left",
}
export interface CartItemRendererProps {
    cartItems: CartItem[];
    restaurantDetails: ReviewedCartApiResponse["restaurant_details"];
    groupTagDetails: ReviewedCartApiResponse["group_tag_details"];
    couponDetails: ReviewedCartApiResponse["coupon_details"];
    cartMenuItemsHeaderList: ReviewedCartApiResponse["cart_menu_items_header_list"];
    itemListHeader?: {
        title: string;
        subtitle: string;
        subtitleColor: string;
        lottieId?: string;
        backgroundColor?: string;
    };
    // TODO: add meal items
    mealItems: [];
    showInputQtyField: boolean;
    isValueMeal?: boolean;
    itemImagesXP?: ItemImagesOnCartXPVariants;
    isIrctcCartSession?: boolean;
    restaurantId?: number | string;
}

export interface GroupCartItemRendererProps extends CartItemRendererProps {
    groupCartId: string;
    isGroupOrderFlow: boolean;
    updateGroupCart: (cartItems: ApiMenuCartItem[]) => void;
    couponDetails: undefined; // coupon details will never come in any cart item for group order
    isHost: boolean;
    isCurrentItemFromSameUser: boolean;
    currentUserId: string;
}

export interface ItemsPresentationConfig {
    titleColor?: keyof IColors;
    background?: string;
}

// Constants for freebie item types
export const OPT_MERCH_FREEBIE_TAG = "OPT_MERCH_FREEBIE";

// Item type constants
export const ITEM_TYPE = {
    FOOD: "food",
    MERCH: "merch",
} as const;

export type ItemType = (typeof ITEM_TYPE)[keyof typeof ITEM_TYPE];
